import _ from 'underscore';
import $ from 'jquery';
import { GridCustomizedPopupEditor } from '@bingads-webui-campaign/grid-customized-popup-editor';
import * as PopupEditor from '@bingads-webui/popup-editor';
import { CampaignuiBaseColumn } from '@bingads-webui-campaign/campaignui-base-column';
import {
  ColumnSpend,
  ColumnClicks,
  ColumnImpressions,
  ColumnCTR,
  ColumnAverageCPC,
  ColumnAverageCPM,
  ColumnAbsoluteTopImpressionRate,
  ColumnConversions,
  ColumnConversionsCredit,
  ColumnConversionRate,
  ColumnPartialConversionRate,
  ColumnRevenueOnAdSpend,
  ColumnAdvertiserReportedRevenue,
  ColumnCPA,
  ColumnPartialConversionCPA,
  ColumnAllConversions,
  ColumnAllConversionsCredit,
  ColumnAllConversionRate,
  ColumnAllPartialConversionRate,
  ColumnAllConversionRevenueOnAdSpend,
  ColumnAllConversionAdvertiserReportedRevenue,
  ColumnAllConversionCPA,
  ColumnAllPartialConversionCPA,
  ColumnCPS,
  ColumnSalesCount,
  ColumnInstalls,
  ColumnCostPerInstall,
  ColumnRevenuePerInstall
} from '@bingads-webui-campaign/campaignui-common-columns';
import { isMSStoreAdsCampaign } from '@bingads-webui-campaign-react/ms-store-ads-common';
import { TrackingTemplateColumn, CustomParametersColumn, FinalUrlSuffixColumn } from '@bingads-webui-campaign/tracking-template-columns';
import { getColumnConversions } from '@bingads-webui-campaign/column-conversions';
import conditionTemplate from '../../template/grid-column-condition.pug';
import flattenConditionTemplate from '../../template/grid-column-flatten-condition.pug';
import bidTemplate from '../../template/grid-column-bid.pug';
import warningColumnTemplate from '../../template/grid-column-warning.pug';
import adGroupNameTemplate from '../../template/grid-column-adgroup.pug';
import campaignNameTemplate from '../../template/grid-column-campaign.pug';
import { ProductGroupsEditBidsView } from './product-groups-edit-bid';
import { ProductGroupsEditBidBoostView } from './product-groups-edit-bid-boost';
import { ProductGroupPathUtil } from './product-groups-path-util';
import { getInlineEditableSaveFunction } from './product-groups-inline-edit';
import { productGroupLevel } from '../../constant';
import { fetchCampaignData } from '../../campaign-data';

const IsProductGroupBidLandscapeEnabled = deps => _.result(deps.config.get('Permissions'), 'IsProductGroupBidLandscapeEnabled');
const IsBidLessThan5CentsInShoppingCampaignEnabled = deps => _.result(deps.config.get('Permissions'), 'IsBidLessThan5CentsInShoppingCampaignEnabled');
const IsAvgPositionDeprecationPhase1Enabled = deps => _.result(deps.config.get('Permissions'), 'IsAvgPositionDeprecationPhase1Enabled');
const IsCurrentAccountInactive = deps => deps.config.get('CurrentAccountInActive');
const IsReadOnlyMode = deps => deps.config.get('ReadOnlyMode');
const NO_DATA_PLACEHOLDER_NONLEAF = '-';
const NO_DATA_PLACEHOLDER_LEAF = '';

export function shouldEnableEdit(enable, deps) {
  return !IsCurrentAccountInactive(deps) && !IsReadOnlyMode(deps) && enable;
}

export const getSortReturn = (value1, value2, columnKey) => {
  let v1 = value1;
  let v2 = value2;
  if (typeof value1 === 'string' && typeof value2 === 'string' && columnKey === 'Condition/Attribute') {
    v1 = value1.toLowerCase();
    v2 = value2.toLowerCase();
  }

  if (v1 < v2) {
    return -1;
  }
  if (v1 > v2) {
    return 1;
  }
  return 0;
};

const SORT_DIRECTION = [-1, 1];

export const isEveryThingElseRow = item =>
  !!(item && !item.Condition.Attribute && item.Condition.Operand);

export function shouldEnableProductGroupTreeEditForMSStoreAdsCampaign(deps, item) {
  if (isMSStoreAdsCampaign(deps.pageContext.CurrentCampaign)) {
    const operand = _.chain(item).result('value').result('Operand').value();
    return operand !== 'MPN';
  }
  return true;
}

export const productGroupSortIteratee = (item, direction) => {
  if (isEveryThingElseRow(item)) {
    // No matter order, "Everything else in ..." is always in the bottom
    // '~' has a lower priority in ASCII order
    return direction === 1 ? '~' : '';
  }
  return item.Condition.Attribute;
};

const generatePickProperty = key => (item) => {
  if (item.isAggregateRow) {
    return NO_DATA_PLACEHOLDER_LEAF;
  }
  return item.IsLeaf ? item[key] || NO_DATA_PLACEHOLDER_LEAF : NO_DATA_PLACEHOLDER_NONLEAF;
};

// const emptyValueFormatter = (val, noData = NO_DATA_PLACEHOLDER_LEAF) =>
//   (_.isUndefined(val) || _.isNull(val) || _.isEmpty(val) ? noData : val);

const renderBidPopupEditor = (deps, bidEditor, myView, position, newPosition, isTreeView, parentContainerSelector) => { // eslint-disable-line
  const useVNextPosition = Object.keys(newPosition).length !== 0;
  const popupEditor = new GridCustomizedPopupEditor({
    editorView: bidEditor,
    cellPosition: useVNextPosition ? newPosition : position,
    tableContainer: isTreeView ? '.product-groups-table-hierarchy' : '.product-groups-table-list',
    parentContainerSelector: useVNextPosition ? parentContainerSelector : null,
  });

  popupEditor.render();
};

class ProductGroupColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'ProductGroup',
      property: ['Condition', 'Id', 'ParentId', 'IsLeaf', 'IsAutoBidding', 'IsExcluded', 'IsBidLandscapeEnabled', 'ParentConditions', 'BidOption', 'AdGroupId', 'CampaignId'],
      // Used in tree plugin
      title: deps.i18n.getString('ProductGroupsGrid_Column_ProductGroup'),
      sortable: 'Condition/Attribute',
    });
    this.deps = deps;
  }

  pickProperty(item) {
    return item;
  }

  template(item) {
    return conditionTemplate(_.extend({
      i18n: this.deps.i18n,
      showSelectorIcon: shouldEnableEdit(!IsCurrentAccountInactive(this.deps) &&
        !IsReadOnlyMode(this.deps) && (item.hasChildren || item.depth < 7), this.deps) && shouldEnableProductGroupTreeEditForMSStoreAdsCampaign(this.deps, item),
    }, _.pick(item, 'attributeText', 'hasChildren', 'depth', 'isExpanded', 'total')));
  }
}

class ProductGroupFlattenColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'ProductGroup',
      property: ['Condition', 'Id', 'ParentId', 'IsAutoBidding', 'IsLeaf', 'IsExcluded', 'IsBidLandscapeEnabled', 'ParentConditions', 'BidOption', 'AdGroupId', 'CampaignId'],
      // Used in tree plugin
      title: deps.i18n.getString('ProductGroupsGrid_Column_ProductGroup'),
      sortable: 'Condition/Attribute',
    });
    this.deps = deps;
    this.productGroupPathUtil = new ProductGroupPathUtil({ deps });
  }

  pickProperty(item) {
    return item;
  }

  template({ value }) {
    if (value.$total) {
      return flattenConditionTemplate(value);
    }
    const condition = { ...value.Condition };
    const parentConditions = value.ParentConditions;
    condition.Attribute = this.productGroupPathUtil.getDisplayName(condition, parentConditions);
    condition.Path = this.productGroupPathUtil.getPath(parentConditions);
    return flattenConditionTemplate(_.defaults(condition, { i18n: this.deps.i18n }));
  }
}

class CampaignNameColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'CampaignName',
      title: 'ProductGroupsGrid_Column_CampaignName',
      property: ['CampaignName', 'CampaignId'],
      i18n: deps.i18n,
      sortable: 'CampaignName',
    });
    this.cid = deps.pageContext.CurrentCustomer.Id;
    this.aid = deps.pageContext.CurrentAccount.Id;
    this.i18n = deps.i18n;
  }

  get property() {
    return {
      get: (value) => {
        const {
          CampaignId: cmpid,
          CampaignName: name,
        } = value;
        const href = '/vnext/productgroups/list';

        return {
          name,
          href,
          cmpid,
          ariaLabel: this.i18n.getString('Click to scope to campaign {{campaignName}}', { campaignName: name }),
        };
      },
      key: this.config.property,
    };
  }

  get template() { return campaignNameTemplate; }
}

class AdGroupNameColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'AdGroupName',
      title: 'ProductGroupsGrid_Column_AdGroupName',
      property: ['AdGroupName', 'AdGroupId'],
      i18n: deps.i18n,
      sortable: 'AdGroupName',
    });
    this.cid = deps.pageContext.CurrentCustomer.Id;
    this.aid = deps.pageContext.CurrentAccount.Id;
    this.uid = deps.pageContext.CurrentUser.Id;
    this.deps = deps;
    this.i18n = deps.i18n;
  }

  get property() {
    return {
      get: (value) => {
        const name = value.AdGroupName;
        const adgid = value.AdGroupId;
        const currentCampaign = this.deps.pageContext.CurrentCampaign;
        // In campaign level, there is no CampaignId in row value
        const cmpid = value.CampaignId || (currentCampaign && currentCampaign.Id);
        const href = '/campaign/vnext/productgroups/list';

        return {
          name,
          href,
          adgid,
          cmpid,
          ariaLabel: this.i18n.getString('Click to scope to ad group {{adGroupName}}', { adGroupName: name }),
        };
      },
      key: this.config.property,
    };
  }

  get template() { return adGroupNameTemplate; }
}

function getBidOption(bidOption) {
  if (bidOption == null) {
    return 'BidValue';
  }
  return bidOption;
}

class BidColumn extends CampaignuiBaseColumn {
  constructor({
    deps, view, isTreeView, parentContainerSelector,
  }) {
    const title = deps.i18n.getString('ProductGroupsGrid_Column_Bid');

    super({
      name: 'Bid',
      title,
      property: 'BidAmount',
      sortable: {
        direction: SORT_DIRECTION,
        key: 'BidAmount/Bid/Amount',
      },
      helpId: 'Page_CampaignGrids_CurrentBid',
      headClasses: ['text-right'],
      bodyClasses: ['text-right'],
      enableEdit: item => shouldEnableEdit(getBidOption(item.BidOption) === 'BidValue' && item.IsLeaf && (item.IsLeaf || item.BidAmount.Bid.Amount != null), deps),
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.view = view;
    this.deps = deps;
    this.isTreeView = isTreeView;
    this.parentContainerSelector = parentContainerSelector;
  }

  get property() {
    return {
      get: item => item,
      key: 'BidAmount',
    };
  }

  template({ value }) {
    const showBidValue = value.BidAmount && value.BidAmount.Bid &&
      value.BidAmount.Bid.Amount != null && value.IsLeaf && !value.IsExcluded;
    const template = bidTemplate(value.isAggregateRow ? {
      isAggregateRow: true,
    } : {
      i18n: this.deps.i18n,
      isLeaf: value.IsLeaf,
      isExcluded: value.IsExcluded,
      // Only leaf node which is not excluded has valid bid
      bidString: showBidValue ? this.deps.decimal.fromFloatToString(value.BidAmount.Bid.Amount) : '',
      isBidLandscapeAvailable: IsProductGroupBidLandscapeEnabled(this.deps)
        && value.IsBidLandscapeEnabled,
      isAggregateRow: false,
      isAutoBidding: value.IsAutoBidding,
    });
    // if the template is empty string, the grid cell will show a value of '[object Object]'
    return getBidOption(value.BidOption) === 'BidValue' ? template : '<span></span>';
  }

  async onPrompt({
    position, item, event, newPosition,
  }) {
    if ($(event.target).is(`${this.isTreeView ? '.product-groups-table-hierarchy' : '.product-groups-table-list'} .bidlandscape_icon`)) {
      return;
    }
    // account level product group grid item have no campaign info to know if it is MSStoreAds. so read it from campaign api if pageContext have no campaign info
    this.setLoading(event.target, true);
    const campaign = await fetchCampaignData({
      pageContext: this.deps.pageContext,
      campaignId: item.CampaignId,
    });
    this.setLoading(event.target, false);

    const bidEditor = new ProductGroupsEditBidsView(_.extend(
      _.pick(item, 'BidAmount', 'IsExcluded', 'IsAutoBidding'),
      _.pick(this.view, 'campaignId', 'adgroupId'),
      {
        view: this.view,
        deps: this.deps,
        selectedItems: [item],
        isInlineEdit: true,
        campaign,
      }
    )).render();

    renderBidPopupEditor(
      this.deps,
      bidEditor,
      this.view,
      position,
      newPosition,
      this.isTreeView,
      this.parentContainerSelector
    );
  }

  setLoading(el, isLoading) {
    const loadingElement = '<span class="immersive-translate-input-loading" style="position: absolute; left: 0; top: 0" />';
    if (isLoading) {
      $(el).closest('.grid-column-Bid').css('position', 'relative').append(loadingElement);
    } else {
      $(el).closest('.grid-column-Bid').children('.immersive-translate-input-loading').remove();
    }
  }
}

class BidBoostColumn extends CampaignuiBaseColumn {
  constructor({
    deps, view, isTreeView, parentContainerSelector,
  }) {
    const title = deps.i18n.getString('ProductGroupsGrid_Column_BidBoost');

    super({
      name: 'BidBoost',
      title,
      property: 'BidBoost',
      sortable: {
        direction: SORT_DIRECTION,
        key: 'BidBoost/Multiplier',
      },
      helpId: 'pop_BA_BMC_BidBoost',
      headClasses: ['text-right'],
      bodyClasses: ['text-right'],
      enableEdit: item => shouldEnableEdit(getBidOption(item.BidOption) === 'BidBoost' && (item.IsLeaf && (item.IsExcluded || item.BidBoost.Multiplier != null)), deps),
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.deps = deps;
    this.view = view;
    this.isTreeView = isTreeView;
    this.parentContainerSelector = parentContainerSelector;
  }

  get property() {
    return {
      get: item => item,
      key: 'BidBoost',
    };
  }

  template({ value }) {
    const showBidBoostValue = value.BidBoost && value.BidBoost.Multiplier != null;
    const template = bidTemplate(value.isAggregateRow ? {
      isAggregateRow: true,
    } : {
      i18n: this.deps.i18n,
      isLeaf: value.IsLeaf,
      isExcluded: value.IsExcluded,
      bidString: showBidBoostValue ? this.deps.i18n.formatPercentToFixed(
        value.BidBoost.Multiplier / 100,
        { digits: 2 }
      ) : '',
      isBidLandscapeAvailable: false, // Bid landscape is not available for bid boost
      isAggregateRow: false,
    });
    // if the template is empty string, the grid cell will show a value of '[object Object]'
    return getBidOption(value.BidOption) === 'BidBoost' ? template : '<span></span>';
  }

  onPrompt({ position, item, newPosition }) {
    const bidEditor = new ProductGroupsEditBidBoostView(_.extend(
      _.pick(item, 'BidBoost', 'IsExcluded'),
      _.pick(this.view, 'campaignId', 'adgroupId'),
      {
        view: this.view,
        deps: this.deps,
        selectedItems: [item],
        isInlineEdit: true,
      }
    )).render();

    renderBidPopupEditor(
      this.deps,
      bidEditor,
      this.view,
      position,
      newPosition,
      this.isTreeView,
      this.parentContainerSelector
    );
  }
}

class WarningsColumn extends CampaignuiBaseColumn {
  constructor({ deps, view }) {
    const title = deps.i18n.getString('ProductGroupsGrid_ColumnName_Warnings');

    super({
      name: 'Warnings',
      title,
      property: 'Warnings',
      helpId: 'pop_BA_ProductAds_BidAlerts',
      sortable: false,
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.deps = deps;
    this.view = view;
  }

  get property() {
    return {
      get: item => item,
    };
  }

  template({ value }) {
    const bidRangeKey = IsBidLessThan5CentsInShoppingCampaignEnabled(this.deps) ? 'ProductGroupBidRange' : 'KeywordBidRange';
    const MinimumBid = _.result(this.deps.config.get(bidRangeKey), 'FloorValue');
    const showBidBoostValue = value.BidBoost && value.BidBoost.Multiplier != null;

    return warningColumnTemplate(value.isAggregateRow ? {
      isAggregateRow: true,
    } : {
      i18n: this.deps.i18n,
      hasWarning: value.IsLeaf && !value.IsExcluded && !value.IsAutoBidding &&
        value.BidAmount.Bid.Amount < MinimumBid && !showBidBoostValue,
      warningCount: 1,
    });
  }
}

class DestinationUrlColumn extends CampaignuiBaseColumn {
  constructor({
    deps, view, parentContainerSelector,
  }) {
    const title = deps.i18n.getString('ProductGroupsGrid_Column_DestinationUrl');

    super({
      name: 'DestinationUrl',
      title,
      property: 'DestinationUrl',
      helpId: 'Page_CampaignGrids_DestinationUrl',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
      enableEdit: item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps),
    });
    this.deps = deps;
    this.view = view;
    this.parentContainerSelector = parentContainerSelector;
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }

  onPrompt({ position, item, newPosition }) {
    const useVNextPosition = Object.keys(newPosition).length !== 0;
    const prompt = (template = 'inline') =>
      PopupEditor.prompt({
        position: useVNextPosition ? newPosition : position,
        parentContainerSelector: useVNextPosition ? this.parentContainerSelector : null,
        i18n: this.deps.i18n,
        schema: this.deps.s$ProductGroup,
        item,
        fields: ['DestinationUrl'],
        hasLabels: false,
        template,
        saveModel: (model, editorView) =>
          getInlineEditableSaveFunction(this.deps)({
            view: this.view,
            i18n: this.deps.i18n,
            fieldName: 'DestinationUrl',
            updated: _.extend(model, _.pick(item, 'Id', 'CampaignId', 'AdGroupId')),
            editorView,
            editorViewCallbackAfterUpdate: () => {
              editorView.remove();
            },
          }),
      });
    prompt();
  }
}

class ProductGroupTrackingTemplateColumn extends TrackingTemplateColumn {
  constructor({
    deps, view, parentContainerSelector,
  }) {
    super({
      i18n: deps.i18n,
      view,
      inlineEditableSave: getInlineEditableSaveFunction(deps),
      parentContainerSelector,
    });
    this.config.enableEdit = item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps);
    this.isForVNext = true;
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class ProductGroupFinalUrlSuffixColumn extends FinalUrlSuffixColumn {
  constructor({
    deps, view, parentContainerSelector,
  }) {
    super({
      i18n: deps.i18n,
      view,
      inlineEditableSave: getInlineEditableSaveFunction(deps),
      parentContainerSelector,
    });
    this.config.enableEdit = item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps);
    this.isForVNext = true;
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class ProductGroupCustomParametersColumn extends CustomParametersColumn {
  constructor({
    deps, view, parentContainerSelector,
  }) {
    super({
      i18n: deps.i18n,
      view,
      inlineEditableSave: getInlineEditableSaveFunction(deps),
      permissions: deps.config.get('Permissions'),
      parentContainerSelector,
    });
    this.config.enableEdit = item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps);
    this.isForVNext = true;
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class ProductsSubmittedColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_ProductsSubmitted');

    super({
      name: 'ProductsSubmitted',
      title: 'ProductGroupsGrid_Column_ProductsSubmitted',
      property: 'SubmittedOfferCount',
      format: 'integer',
      helpId: 'pop_BA_BSC_ProductsSubmitted',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'SubmittedOfferCount',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class ProductsTargetedColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_ProductsTargeted');

    super({
      name: 'ProductsTargeted',
      title: 'ProductGroupsGrid_Column_ProductsTargeted',
      property: 'TargetedOfferCount',
      format: 'integer',
      helpId: 'pop_BA_BSC_ProductTargeted',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'TargetedOfferCount',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

function getAttributeColumns({
  view,
  isTreeView,
}) {
  const { isBidValueOption, isBidBoostOption } = view;
  return _.compact([
    isTreeView ? ProductGroupColumn : ProductGroupFlattenColumn,
    view.level === productGroupLevel.Account ? CampaignNameColumn : null,
    view.level === productGroupLevel.Account || view.level === productGroupLevel.Campaign ?
      AdGroupNameColumn : null,
    isBidValueOption ? BidColumn : null,
    isBidBoostOption ? BidBoostColumn : null,
    isBidValueOption ? WarningsColumn : null,
    DestinationUrlColumn,
    ProductGroupTrackingTemplateColumn,
    ProductGroupCustomParametersColumn,
    ProductGroupFinalUrlSuffixColumn,
  ]);
}

function getPerfColumns(deps) {
  const { config } = deps;
  const permissions = config.get('permissions');
  const {
    dynamic: {
      CPSReporting,
    } = {},
  } = permissions;
  return _.compact([
    ColumnSpend,
    ColumnClicks,
    ColumnImpressions,
    ColumnCTR,
    ColumnAverageCPC,
    ColumnAverageCPM,
    IsAvgPositionDeprecationPhase1Enabled(deps) && ColumnAbsoluteTopImpressionRate,
    CPSReporting && ColumnCPS,
  ]);
}

function getConvColumns({ config }) {
  const permissions = config.get('permissions');
  const {
    dynamic: {
      ColumnConversion: IsColumnConversionEnabled,
      PartialConversions: IsPartialConversionsEnabled,
      CPSReporting,
      InstallPerformanceColumns,
    } = {},
  } = permissions;
  const usePartialConversions = IsColumnConversionEnabled && IsPartialConversionsEnabled;

  return _.compact([
    usePartialConversions ? ColumnConversionsCredit : ColumnConversions,
    usePartialConversions ? ColumnPartialConversionRate : ColumnConversionRate,
    ColumnRevenueOnAdSpend,
    ColumnAdvertiserReportedRevenue,
    usePartialConversions ? ColumnPartialConversionCPA : ColumnCPA,
    usePartialConversions ? ColumnAllConversionsCredit : ColumnAllConversions,
    usePartialConversions ? ColumnAllPartialConversionRate : ColumnAllConversionRate,
    ColumnAllConversionRevenueOnAdSpend,
    ColumnAllConversionAdvertiserReportedRevenue,
    usePartialConversions ? ColumnAllPartialConversionCPA : ColumnAllConversionCPA,
    CPSReporting && ColumnSalesCount,
    InstallPerformanceColumns && ColumnInstalls,
    InstallPerformanceColumns && ColumnCostPerInstall,
    InstallPerformanceColumns && ColumnRevenuePerInstall,
  ]);
}

function getShoppingColumns() {
  return [
    ProductsSubmittedColumn,
    ProductsTargetedColumn,
  ];
}

export function getGridColumnsConfig(deps, view, isTreeView) {
  const parentContainerSelector = isTreeView ? '.product-groups-page-hierarchy .grid-and-disclaim-container' :
    '.product-groups-page-list .grid-and-disclaim-container';

  return _.chain([
    ...getAttributeColumns({
      deps,
      view,
      isTreeView,
    }),
    ...getPerfColumns(deps),
    ...getConvColumns(deps),
    ...getShoppingColumns(),
  ])
    .compact()
    .map(Column => (new Column({
      deps, view, isForVNext: true, isTreeView, parentContainerSelector, i18n: deps.i18n,
    }).toJSON()))
    .value();
}

function getCategoryOptions(options, deps, view) {
  return _.map(options, (option) => {
    const column = new option.Column({
      deps,
      view,
      i18n: deps.i18n,
    });

    return {
      key: column.name,
      name: column.title,
      selectOption: option.selectOption,
    };
  });
}

function getColumnNames(Cols, deps, view) {
  return _.map(Cols, Column => new Column({
    deps,
    view,
    i18n: deps.i18n,
  }).name);
}

function getLockedColumns(categories) {
  let lockedColumns = [];
  _.forEach(categories, (category) => {
    lockedColumns = lockedColumns.concat(_.map(_.filter(category.options, option => option.selectOption === 'locked'), value => value.Column));
  });

  return lockedColumns;
}

export function getColumnChooserPluginConfig(deps, view, isTreeView) {
  const currentCampaign = deps.config.get('CurrentCampaign');
  const isSpaSpbCampaignType = currentCampaign &&
    currentCampaign.CampaignSubType === 'ShoppingSponsoredProductAd';
  const permissions = deps.config.get('Permissions');
  const {
    dynamic: {
      ColumnConversion: IsColumnConversionEnabled,
    } = {},
  } = permissions;

  const isLockedColumn = (Column) => {
    const lockColumnWhiteList = _.compact([
      ProductGroupColumn,
      ProductGroupFlattenColumn,
      BidColumn,
      BidBoostColumn,
      WarningsColumn,
      AdGroupNameColumn,
      CampaignNameColumn,
      isSpaSpbCampaignType ? ProductsTargetedColumn : null,
    ]);
    return _.contains(lockColumnWhiteList, Column) ? 'locked' : 'optional';
  };

  const categories = [{
    name: deps.i18n.getString('Attributes'),
    options: _.compact(_.map(getAttributeColumns({ deps, view, isTreeView }), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    }))),
  }, {
    name: deps.i18n.getString('Performance'),
    options: _.map(getPerfColumns(deps), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    })),
  }, {
    name: deps.i18n.getString('Conversions'),
    options: _.map(getConvColumns(deps), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    })),
  }, {
    name: deps.i18n.getString('ColumnCategory_Shopping'),
    options: _.map(getShoppingColumns(), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    })),
  }];

  return {
    categories: _.map(categories, category => ({
      name: category.name,
      options: getCategoryOptions(category.options, deps, view),
    })),
    selected: getColumnNames([
      ...getAttributeColumns({ deps, view }),
      ...getPerfColumns(deps),
      ...getConvColumns(deps),
      ...getShoppingColumns(),
    ], deps, view),
    locked: getColumnNames(getLockedColumns(categories), deps, view),
    patchQuery: true,
    Columns: deps.Columns,
    SelectedColumns: deps.SelectedColumns,
    LegacyColumns: deps.LegacyColumns,
    LegacySelectedColumns: deps.LegacySelectedColumns,
    i18n: deps.i18n,
    ...IsColumnConversionEnabled ? {
      columnConversions: getColumnConversions(getGridColumnsConfig(deps, view, isTreeView)),
    } : {},
  };
}
